import { Fragment, useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import type { Publicacao } from '../publicacao.types';
import { publicacaoService } from '../publicacaoService';
import { FORM_VAZIO, formDe } from './publicacaoEditor';
import type { FormPublicacao } from './publicacaoEditor';
import { EditorConteudo } from './EditorConteudo';
import { EditorCienciaDestaque } from './EditorCienciaDestaque';
import { useSalvarPublicacao } from './useSalvarPublicacao';
import type { AcaoSalvar } from './useSalvarPublicacao';

interface PublicacaoEditorDrawerProps {
  isOpen: boolean;
  /** null = nova publicação. */
  publicacao: Publicacao | null;
  /** Todas (pra checar o limite de fixadas). */
  todas: Publicacao[];
  onClose: () => void;
}

/** Editor (drawer, como os do SI): conteúdo + ciência/destaque/quando publicar. */
export function PublicacaoEditorDrawer({ isOpen, publicacao, todas, onClose }: PublicacaoEditorDrawerProps) {
  const [form, setForm] = useState<FormPublicacao>(FORM_VAZIO);
  // Id reservado ao abrir (novo) — os anexos sobem na hora, já na pasta da publicação.
  const [idReservado, setIdReservado] = useState(() => publicacaoService.novoId());
  const { salvar, salvando, erros, limparErros } = useSalvarPublicacao(todas);
  const status = publicacao?.status ?? 'rascunho';
  const jaPublicada = status === 'publicada' || status === 'despublicada';

  useEffect(() => {
    if (isOpen) {
      setForm(publicacao ? formDe(publicacao) : FORM_VAZIO);
      setIdReservado(publicacao?.id ?? publicacaoService.novoId());
      limparErros();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, publicacao?.id]);

  const alterar = (parcial: Partial<FormPublicacao> | ((atual: FormPublicacao) => Partial<FormPublicacao>)) =>
    setForm((atual) => ({ ...atual, ...(typeof parcial === 'function' ? parcial(atual) : parcial) }));
  /** Fechar sem salvar: o que subiu nesta edição não fica órfão no Storage. */
  const fechar = () => {
    if (salvando) return;
    form.pathsEnviados.forEach((path) => void publicacaoService.removerArquivo(path));
    onClose();
  };
  const executar = async (acao: AcaoSalvar) => {
    const id = await salvar(form, publicacao, acao, idReservado);
    if (id) onClose();
  };

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog onClose={fechar} className="relative z-50">
        <Transition.Child
          as={Fragment}
          enter="transition-opacity ease-linear duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity ease-linear duration-300"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        </Transition.Child>
        <div className="fixed inset-0 flex justify-end">
          <Transition.Child
            as={Fragment}
            enter="transition ease-in-out duration-300 transform"
            enterFrom="translate-x-full"
            enterTo="translate-x-0"
            leave="transition ease-in-out duration-300 transform"
            leaveFrom="translate-x-0"
            leaveTo="translate-x-full"
          >
            <Dialog.Panel className="flex h-full w-full max-w-2xl flex-col overflow-hidden bg-white shadow-xl">
              <div className="flex items-center justify-between border-b border-raro-gray-200 p-4">
                <Dialog.Title className="font-montserrat text-lg font-semibold text-raro-gray-900">
                  {publicacao ? 'Editar publicação' : 'Nova publicação'}
                </Dialog.Title>
                <button type="button" className="text-raro-gray-400 hover:text-raro-gray-500" onClick={fechar}>
                  <span className="sr-only">Fechar</span>
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="flex-1 space-y-6 overflow-y-auto p-5">
                <EditorConteudo form={form} onChange={alterar} publicacaoId={idReservado} />
                <EditorCienciaDestaque form={form} onChange={alterar} status={status} />
                {jaPublicada && (
                  <p className="rounded-lg bg-raro-gray-50 px-3 py-2 text-xs text-raro-gray-600">
                    Editar não pede a ciência de novo — quem já confirmou continua valendo. Para mudanças importantes, use
                    &quot;Pedir nova ciência&quot; na lista.
                  </p>
                )}
              </div>

              {erros.length > 0 && (
                <ul className="space-y-0.5 border-t border-rose-100 bg-rose-50 px-5 py-2 text-xs text-rose-700">
                  {erros.map((erro) => (
                    <li key={erro}>• {erro}</li>
                  ))}
                </ul>
              )}
              <div className="flex flex-wrap justify-end gap-3 border-t border-raro-gray-200 p-4">
                {jaPublicada ? (
                  <button type="button" className="btn-primary" disabled={salvando} onClick={() => executar('salvar')}>
                    {salvando ? 'Salvando...' : 'Salvar alterações'}
                  </button>
                ) : (
                  <>
                    <button type="button" className="btn-secondary" disabled={salvando} onClick={() => executar('rascunho')}>
                      Salvar rascunho
                    </button>
                    <button type="button" className="btn-primary" disabled={salvando} onClick={() => executar('publicar')}>
                      {salvando ? 'Salvando...' : form.agendar ? 'Agendar' : 'Publicar agora'}
                    </button>
                  </>
                )}
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}
